import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../utils/apiClient';

// Address interface for tax calculation
export interface AddressData {
  address_line1: string;
  address_line2?: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

// Define Product interface based on OpenAPI schema
interface Product {
  id: string;
  name: string;
  description: string;
  trial_period_days: number;
  is_active: boolean;
  created_at?: string;
  trial_eligibility?: {
    has_had_trial: boolean;
    trial_available: boolean;
    trial_start_date: string | null;
    trial_end_date: string | null;
  };
}

// Updated to match OpenAPI schema
interface SubscriptionTier {
  id: string;
  product: string;  // This is just the ID, not the full product object
  name: string;
  description: string;
  stripe_price_id_monthly: string;
  stripe_price_id_annual: string;
  monthly_price_usd: number;
  annual_price_usd: number;
  features: Record<string, any>;
  created_at?: string;
}

// Define the subscription data type
interface SubscriptionData {
  subscriptions: any[] | null;
  timestamp: number;
}

// Define the product cache type
interface ProductCache {
  data: Product | null;
  timestamp: number;
}

interface SubscriptionContextType {
  hasActiveSubscription: boolean;
  hasPastDueSubscription: boolean;
  isLoading: boolean;
  product: Product | null;
  subscriptionTiers: SubscriptionTier[];
  hasHadTrial: boolean;
  createSetupIntent: () => Promise<{ clientSecret: string }>;
  createSubscription: (paymentMethodId: string, tierId: string, address: AddressData, discountCode?: string) => Promise<void>;
  fetchProduct: () => Promise<Product>;
  fetchSubscriptionTiers: (forceCheck?: boolean) => Promise<SubscriptionTier[]>;
  checkSubscriptionStatus: (forceCheck?: boolean) => Promise<boolean>;
  cancelSubscription: (subscriptionId: string, reason: string) => Promise<{ success: boolean, end_date?: string }>;
  getCurrentSubscription: () => Promise<any>;
  checkTrialStatus: () => Promise<boolean>;
  updatePaymentMethod: (paymentMethodId: string) => Promise<{ success: boolean, recovered_subscriptions: string[] }>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

// Product ID from environment variable
const PRODUCT_ID = process.env.REACT_APP_PRODUCT_ID;

if (!PRODUCT_ID) {
  console.error('❌ REACT_APP_PRODUCT_ID environment variable is not set');
}

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, refreshAccessToken } = useAuth();
  const navigate = useNavigate();
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [hasPastDueSubscription, setHasPastDueSubscription] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [product, setProduct] = useState<Product | null>(null);
  const [subscriptionTiers, setSubscriptionTiers] = useState<SubscriptionTier[]>([]);
  const [hasHadTrial, setHasHadTrial] = useState(false);
  
  // Add state to track last product check time
  const [lastProductCheckTime, setLastProductCheckTime] = useState<number>(0);
  // Add state to track last subscription tiers check time
  const [lastTiersCheckTime, setLastTiersCheckTime] = useState<number>(0);
  // Add state to track if we're currently retrying a request
  const [isRetrying, setIsRetrying] = useState(false);
  // Add a state to track when we're checking a recently created subscription
  const [recentlyCreatedSubscription, setRecentlyCreatedSubscription] = useState(false);
  
  // Create a ref to store cached subscription data
  const subscriptionCache = useRef<SubscriptionData>({
    subscriptions: null,
    timestamp: 0
  });

  // Create a ref to store cached product data
  const productCache = useRef<ProductCache>({
    data: null,
    timestamp: 0
  });
  
  // Create a ref to track pending requests
  const pendingSubscriptionsRequest = useRef<Promise<any[]> | null>(null);

  // Fetch product information with caching
  const fetchProduct = useCallback(async (forceCheck = false): Promise<Product> => {
    // Use cached product unless forced
    const now = Date.now();
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

    if (!forceCheck && 
        productCache.current.data && 
        now - productCache.current.timestamp < CACHE_DURATION) {
      console.log('🔄 Using cached product data');
      
      // Update trial status from cached product data if available
      if (productCache.current.data.trial_eligibility) {
        setHasHadTrial(productCache.current.data.trial_eligibility.has_had_trial);
      }
      
      return productCache.current.data;
    }

    console.log('🌐 Fetching product information...');
    
    try {
      const productData = await api.get<Product>(`/products/${PRODUCT_ID}/`);
      
      // Update trial status from product data if available
      if (productData.trial_eligibility) {
        setHasHadTrial(productData.trial_eligibility.has_had_trial);
      }
      
      // Update cache and state
      productCache.current = {
        data: productData,
        timestamp: now
      };
      setProduct(productData);
      setLastProductCheckTime(now);
      
      return productData;
    } catch (error) {
      console.error('❌ Error fetching product:', error);
      throw error;
    }
  }, []);

  // Fetch subscription tiers for the product
  const fetchSubscriptionTiers = useCallback(async (forceCheck = false): Promise<SubscriptionTier[]> => {
    // Use cached tiers unless forced
    const now = Date.now();
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

    if (!forceCheck && subscriptionTiers.length > 0 && now - lastTiersCheckTime < CACHE_DURATION) {
      console.log('🔄 Using cached subscription tiers');
      return subscriptionTiers;
    }

    console.log('🌐 Fetching subscription tiers...');
    
    try {
      const allTiers = await api.get<SubscriptionTier[]>('/subscription-tiers/');
      
      // Filter tiers for our specific product
      const productTiers = allTiers.filter(tier => tier.product === PRODUCT_ID);
      
      if (productTiers.length > 0) {
        setSubscriptionTiers(productTiers);
        setLastTiersCheckTime(now);
      }
      
      return productTiers;
    } catch (error) {
      console.error('❌ Error fetching subscription tiers:', error);
      return [];
    }
  }, [subscriptionTiers, lastTiersCheckTime]);

  // Load product and tiers on mount
  useEffect(() => {
    if (user) {
      // Only fetch if we don't have cached data or if it's expired
      const now = Date.now();
      const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

      if (!productCache.current.data || 
          now - productCache.current.timestamp >= CACHE_DURATION) {
        fetchProduct().catch(console.error);
      } else {
        // Use cached data
        setProduct(productCache.current.data);
      }

      if (!subscriptionTiers.length || 
          now - lastTiersCheckTime >= CACHE_DURATION) {
        fetchSubscriptionTiers().catch(console.error);
      }
    }
  }, [user, fetchProduct, fetchSubscriptionTiers, lastTiersCheckTime]);

  // Shared function to fetch subscriptions with caching
  const fetchSubscriptions = useCallback(async (forceCheck = false): Promise<any[]> => {
    // Early return if not authenticated
    if (!user) {
      console.log('Not fetching subscriptions: user not authenticated');
      return [];
    }
    
    const now = Date.now();
    const CACHE_DURATION = 60 * 1000; // 1 minute in milliseconds
    
    // Use cached data if available and not expired
    if (
      !forceCheck && 
      subscriptionCache.current.subscriptions && 
      now - subscriptionCache.current.timestamp < CACHE_DURATION
    ) {
      console.log('🔄 Using cached subscription data');
      return subscriptionCache.current.subscriptions;
    }
    
    // If there's already a request in progress, return that instead of starting a new one
    if (pendingSubscriptionsRequest.current) {
      console.log('⏳ Reusing pending subscriptions request');
      return pendingSubscriptionsRequest.current;
    }
    
    console.log('🌐 Fetching subscriptions from API...');
    setIsLoading(true);
    
    // Create a new request and store it
    pendingSubscriptionsRequest.current = api.get<any[]>('/subscriptions/')
      .then(subscriptions => {
        // Update the cache with new data
        subscriptionCache.current = {
          subscriptions: subscriptions || [],
          timestamp: Date.now()
        };
        setIsLoading(false);
        return subscriptions || [];
      })
      .catch(error => {
        console.error('Error fetching subscriptions:', error);
        setIsLoading(false);
        return [];
      })
      .finally(() => {
        // Clear the pending request
        pendingSubscriptionsRequest.current = null;
      });
    
    return pendingSubscriptionsRequest.current;
  }, [user]);

  // Check subscription status
  const checkSubscriptionStatus = useCallback(async (forceCheck = false): Promise<boolean> => {
    try {
      const subscriptions = await fetchSubscriptions(forceCheck);
      
      // Check if there's at least one active or trialing subscription
      const hasActive = Array.isArray(subscriptions) && subscriptions.some(
        subscription => ['active', 'trialing'].includes(subscription.status)
      );
      
      // Check if there's at least one past_due subscription
      const hasPastDue = Array.isArray(subscriptions) && subscriptions.some(
        subscription => subscription.status === 'past_due'
      );
      
      console.log(`Subscription status: ${hasActive ? 'Active ✅' : 'Inactive ❌'}${hasPastDue ? ' (Past Due ⚠️)' : ''}`);
      setHasActiveSubscription(hasActive);
      setHasPastDueSubscription(hasPastDue);
      
      return hasActive;
    } catch (error) {
      console.error('❌ Error checking subscription status:', error);
      return false;
    }
  }, [fetchSubscriptions]);

  // Get current subscription - now uses the shared fetchSubscriptions function
  const getCurrentSubscription = useCallback(async () => {
    console.log('Getting current subscription...');
    
    try {
      const subscriptions = await fetchSubscriptions();
      
      if (!Array.isArray(subscriptions) || subscriptions.length === 0) {
        console.log('No subscriptions found');
        return null;
      }
      
      // Find the first active or trialing subscription
      const currentSubscription = subscriptions.find(
        subscription => ['active', 'trialing'].includes(subscription.status)
      );
      
      if (currentSubscription) {
        console.log('Found active subscription:', currentSubscription.id);
      } else {
        console.log('No active subscription found');
      }
      
      return currentSubscription || null;
    } catch (error) {
      console.error('❌ Error getting current subscription:', error);
      return null;
    }
  }, [fetchSubscriptions]);

  // Create setup intent
  const createSetupIntent = useCallback(async (): Promise<{ clientSecret: string }> => {
    console.log('🌐 Creating setup intent (Stripe)...');

    try {
      const response = await api.post<{ client_secret: string }>('/subscriptions/create_setup_intent/');
      console.log('✅ Setup intent created successfully');
      return { clientSecret: response.client_secret };
    } catch (error) {
      console.error('❌ Error creating setup intent:', error);
      throw error;
    }
  }, []);

  // Create subscription
  const createSubscription = useCallback(async (paymentMethodId: string, tierId: string, address: AddressData, discountCode?: string): Promise<void> => {
    console.log('🌐 Creating subscription (Stripe)...');

    try {
      const trimmedDiscountCode = discountCode?.trim();
      const response = await api.post('/subscriptions/create_subscription/', {
        payment_method_id: paymentMethodId,
        tier_id: tierId,
        ...(trimmedDiscountCode ? { discount_code: trimmedDiscountCode } : {}),
        ...address // Include all address fields
      });
      
      console.log('✅ Subscription created successfully', response);
      
      // Mark that we've created a subscription recently so polling starts
      setRecentlyCreatedSubscription(true);
      setHasActiveSubscription(true);
      
      // If this was a trial subscription, update the trial status
      if (!hasHadTrial) {
        setHasHadTrial(true);
      }
      
      // Invalidate the subscription cache
      subscriptionCache.current = {
        subscriptions: null,
        timestamp: 0
      };
      
      return response;
    } catch (error) {
      console.error('❌ Error creating subscription:', error);
      throw error;
    }
  }, [hasHadTrial]);

  // Cancel subscription
  const cancelSubscription = useCallback(async (subscriptionId: string, reason: string): Promise<{ success: boolean, end_date?: string }> => {
    console.log('🌐 Cancelling subscription:', subscriptionId);
    
    try {
      // Use our standard API client to make the cancellation request
      const response = await api.post(`/subscriptions/${subscriptionId}/cancel/`, {
        cancel_immediately: false,
        cancellation_reason: reason
      });
      
      console.log('✅ Subscription cancelled successfully:', response);
      
      // Invalidate the subscription cache
      subscriptionCache.current = {
        subscriptions: null,
        timestamp: 0
      };
      
      return { 
        success: true, 
        end_date: response.cancel_at
      };
    } catch (error) {
      console.error('❌ Error cancelling subscription:', error);
      // Log basic error details
      if (error instanceof Error) {
        console.error('Error message:', error.message);
      }
      return { success: false };
    }
  }, []);

  // Update payment method
  const updatePaymentMethod = useCallback(async (paymentMethodId: string): Promise<{ success: boolean, recovered_subscriptions: string[] }> => {
    console.log('🌐 Updating payment method...');
    
    try {
      // Use our standard API client to make the update request
      const response = await api.post('/subscriptions/update_payment_method/', {
        payment_method_id: paymentMethodId
      });
      
      console.log('✅ Payment method updated successfully:', response);
      
      // Invalidate the subscription cache so we fetch fresh data
      subscriptionCache.current = {
        subscriptions: null,
        timestamp: 0
      };
      
      // Refresh subscription status
      await checkSubscriptionStatus(true);
      
      return { 
        success: true,
        recovered_subscriptions: response.recovered_subscriptions || []
      };
    } catch (error) {
      console.error('❌ Error updating payment method:', error);
      // Log basic error details
      if (error instanceof Error) {
        console.error('Error message:', error.message);
      }
      return { success: false, recovered_subscriptions: [] };
    }
  }, [checkSubscriptionStatus]);

  // Check subscription status on auth changes
  useEffect(() => {
    if (user) {
      checkSubscriptionStatus();
    } else {
      setHasActiveSubscription(false);
    }
  }, [user, checkSubscriptionStatus]);

  // Add function to check trial status
  const checkTrialStatus = useCallback(async (): Promise<boolean> => {
    try {
      // Force fetch the product to get the latest trial eligibility
      const productData = await fetchProduct(true);
      
      if (productData && productData.trial_eligibility) {
        const hasHadTrial = productData.trial_eligibility.has_had_trial;
        setHasHadTrial(hasHadTrial);
        return hasHadTrial;
      }
      
      return false;
    } catch (error) {
      console.error('Error checking trial status:', error);
      return false;
    }
  }, [fetchProduct]);

  // Check trial status on mount and when user changes
  useEffect(() => {
    if (user) {
      // We'll fetch the product which will update the trial status
      fetchProduct().catch(console.error);
    }
  }, [user, fetchProduct]);

  // Clear caches when logging out
  useEffect(() => {
    if (!user) {
      productCache.current = {
        data: null,
        timestamp: 0
      };
      subscriptionCache.current = {
        subscriptions: null,
        timestamp: 0
      };
      setProduct(null);
      setSubscriptionTiers([]);
    }
  }, [user]);

  const value = {
    hasActiveSubscription,
    hasPastDueSubscription,
    isLoading,
    product,
    subscriptionTiers,
    hasHadTrial,
    createSetupIntent,
    createSubscription,
    fetchProduct,
    fetchSubscriptionTiers,
    checkSubscriptionStatus,
    cancelSubscription,
    getCurrentSubscription,
    checkTrialStatus,
    updatePaymentMethod,
  };

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}; 